import { Coachmark, useCoachmark } from 'coachmark'
import { Button, Interactive } from '@highlight/ui'
import type { MeetingTourStepDescriptor } from '../build-meeting-tour-steps'

interface TourStepCardProps {
  step: MeetingTourStepDescriptor
  /**
   * The last step's Done. The library's own `finish()` never reaches
   * `onOpenChange` (that only fires for Base UI-initiated closes: Escape,
   * outside click, the Close part), so completion has to be reported from the
   * button that causes it.
   */
  onFinish: () => void
}

/**
 * One step's popup: positioner, card surface, and the shared step chrome
 * (counter, navigation). Rendered inside its `Coachmark.Step`, so the library
 * anchors it to the step's target and swaps it whole between steps.
 */
export function TourStepCard({ step, onFinish }: TourStepCardProps) {
  return (
    // Above the footer chrome and the backdrop. The popup portals to the
    // document body, outside the panel's own layer management.
    <Coachmark.Positioner side={step.side} align={step.align ?? 'center'} sideOffset={12} className="z-[110]">
      <Coachmark.Popup className="bg-background corner-smooth w-[300px] rounded-2xl shadow-xl outline-none">
        {/* The popup portals outside the panel's registered bounds, so it has
            to mark its own box for the overlay's cursor hit-test. */}
        <Interactive>
          <TourStepCardContent step={step} onFinish={onFinish} />
        </Interactive>
      </Coachmark.Popup>
    </Coachmark.Positioner>
  )
}

function TourStepCardContent({ step, onFinish }: TourStepCardProps) {
  const { stepIndex, stepCount, isFirstStep, isLastStep } = useCoachmark()

  return (
    <div className="relative flex flex-col gap-2 px-4 pb-4 pt-3">
      <Coachmark.Title className="text-foreground my-0 pr-12 text-sm font-semibold">{step.title}</Coachmark.Title>
      <span className="text-foreground-secondary type-ui-xs absolute right-4 top-3.5 font-medium tabular-nums">
        {stepIndex + 1} of {stepCount}
      </span>
      <Coachmark.Description className="text-foreground my-0 text-sm">{step.description}</Coachmark.Description>
      <div className="flex items-center justify-between gap-2 pt-1">
        {/* Skip persists as "dismissed" via the root's close handling. */}
        {!isLastStep ? <Coachmark.Close render={<Button variant="ghost" />}>Skip</Coachmark.Close> : <span />}
        <div className="flex items-center gap-1.5">
          {!isFirstStep && <Coachmark.Previous render={<Button variant="outline" />}>Back</Coachmark.Previous>}
          {isLastStep ? (
            <Coachmark.Next render={<Button variant="primary" onClick={onFinish} />}>Done</Coachmark.Next>
          ) : (
            <Coachmark.Next render={<Button variant="primary" />}>Continue</Coachmark.Next>
          )}
        </div>
      </div>
    </div>
  )
}
